import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { TimeSlotInfo, ServiceInfo } from '../types/reservation.types';

export function useAvailability(restaurantId: string) {
  const [slots, setSlots] = useState<TimeSlotInfo[]>([]);
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailability = useCallback(async (date: Date, partySize: number) => {
    setLoading(true);
    setError(null);
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const { data, error: fnError } = await supabase.functions.invoke('check-availability', {
        body: { restaurantId, date: formattedDate, partySize },
      });

      if (fnError) throw fnError;

      // Map slots to enriched status
      const enrichedSlots: TimeSlotInfo[] = (data.slots || []).map((s: any) => ({
        time: s.time,
        remainingCapacity: s.remainingCapacity ?? 0,
        service: s.service,
        status: !s.available
          ? 'full'
          : s.remainingCapacity <= 3
          ? 'few_left'
          : 'available',
      }));

      setSlots(enrichedSlots);

      // Derive services from slots
      const lunchSlots = enrichedSlots.filter(s => s.service === 'lunch');
      const dinnerSlots = enrichedSlots.filter(s => s.service === 'dinner');
      const svc: ServiceInfo[] = [];
      if (lunchSlots.length > 0) svc.push({ name: 'Déjeuner', start: lunchSlots[0].time, end: lunchSlots[lunchSlots.length - 1].time });
      if (dinnerSlots.length > 0) svc.push({ name: 'Dîner', start: dinnerSlots[0].time, end: dinnerSlots[dinnerSlots.length - 1].time });
      setServices(svc);
    } catch (err: any) {
      console.error('Availability error:', err);
      setError('Impossible de charger les créneaux');
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  return { slots, services, loading, error, fetchAvailability };
}
